import { supabase } from "@/integrations/supabase/client";
import { extractCityStateFromGeocodeResults } from "@/lib/locationDetection";

export interface ResolvedPlace {
  city: string;
  state: string;
  /** "City, ST" when both are known, otherwise whichever part we have. */
  label: string;
}

/** Analytics precision: ~1km. Never send a raw coordinate to PostHog. */
export function roundCoord(value: number): number | null {
  if (!Number.isFinite(value)) return null;
  return Math.round(value * 100) / 100;
}

/** Request precision: ~110m. Never send a raw coordinate to the geocoder. */
function geocodeKey(lat: number, lng: number): string {
  return `${Math.round(lat * 1000) / 1000},${Math.round(lng * 1000) / 1000}`;
}

interface GeocodeComponent { short_name?: string; types?: string[] }
interface GeocodeRow { address_components?: GeocodeComponent[] }

function findState(results: unknown): string {
  if (!Array.isArray(results)) return "";
  for (const row of results as GeocodeRow[]) {
    for (const component of row?.address_components ?? []) {
      if (component.types?.includes("administrative_area_level_1")) {
        return component.short_name ?? "";
      }
    }
  }
  return "";
}

const CACHE_PREFIX = "grounded_place_";
const memoryCache = new Map<string, ResolvedPlace | null>();

function readCache(key: string): ResolvedPlace | null | undefined {
  if (memoryCache.has(key)) return memoryCache.get(key);
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return undefined;
    return JSON.parse(raw) as ResolvedPlace;
  } catch {
    return undefined;
  }
}

function writeCache(key: string, value: ResolvedPlace | null) {
  memoryCache.set(key, value);
  if (!value) return;
  try {
    sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    /* private mode: memory cache still applies for this session */
  }
}

/**
 * Turns coordinates into a real place name using the existing reverse
 * geocoder. Cached per rounded coordinate so a browsing session makes at most
 * one call per area. Fails soft: returns null and callers keep their current
 * behaviour.
 */
export async function resolvePlaceName(
  lat: number,
  lng: number,
): Promise<ResolvedPlace | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const key = geocodeKey(lat, lng);
  const cached = readCache(key);
  if (cached !== undefined) return cached;

  try {
    const { data, error } = await supabase.functions.invoke("google-places", {
      body: { action: "reverseGeocode", lat, lng },
    });
    if (error) throw error;

    const { city, state: parsedState } = extractCityStateFromGeocodeResults(data?.results);
    // The shared parser returns as soon as it finds a locality, so the state
    // component is often still unseen. Scan for it independently.
    const state = parsedState || findState(data?.results);
    if (!city && !state) {
      writeCache(key, null);
      return null;
    }

    const resolved: ResolvedPlace = {
      city,
      state,
      label: city && state ? `${city}, ${state}` : city || state,
    };
    writeCache(key, resolved);
    return resolved;
  } catch {
    writeCache(key, null);
    return null;
  }
}
